/**
 * Il login su TELEGRAM WEB (Web A e Web K) in una finestra di PRIMO livello (la «B2»: tma-orcr-101, scelta di Luca L-3931 (a),
 * I-1301, 31/10). SOLO staging: il flag e' acceso solo in `.env.staging` (`docs/claude/variabili/VITE_FEATURE_LOGIN_FINESTRA_ENABLED.md`).
 *
 * ═══ IL PROBLEMA ═══
 * Su Telegram Web la mini app e' un IFRAME di `web.telegram.org`. La strada desktop (`startTelegramSignIn`: la webview naviga SE
 * STESSA su Logto) li' non arriva al login: la `/sign-in` di Logto ha `frame-ancestors` senza `web.telegram.org` (finisce su
 * `chrome-error://`), e i cookie dell'interazione sono `SameSite=Lax`, che in un iframe cross-site non viaggiano.
 *
 * ═══ LA STRADA ═══
 * Al gesto su «Accedi» l'iframe apre una finestra di primo livello, e le da' l'URL di Logto. Il `redirect_uri` e' quello di sempre
 * (`<origin>/auth/callback`): nessuna modifica a Logto. Il callback, nella finestra, riconosce il flusso dal PREFISSO dello `state`
 * (lo `state` e' l'unica cosa nostra che attraversa Logto: lo storage della finestra NON e' quello dell'iframe, che e' partizionato
 * sotto `web.telegram.org`, e `window.name` i browser lo azzerano nelle navigazioni cross-site), manda `{code, state}` all'opener con
 * `postMessage` all'origine ESATTA della TMA e si chiude. L'iframe, che ha verifier e state nel SUO storage, confronta lo state e
 * scambia il codice (`exchangeInAppAuthCode`).
 *
 * ═══ LE MISURE DA CUI DIPENDE (31/10) ═══
 * - L'iframe della mini app ha `allow-popups` ma NON `allow-popups-to-escape-sandbox` (Web A `WebAppTab.tsx:1254` +
 *   `util/browser/iframe.ts:1-10` @ 6002824b; Web K `webApp.tsx:55-62,1065` @ c2c6e5e7): la finestra EREDITA la sandbox. Con
 *   `allow-scripts allow-forms allow-same-origin` Logto regge (si naviga da sola, ha l'origine vera, e' di primo livello: i cookie
 *   Lax sono di prima parte).
 * - ⚠️ Sotto sandbox una risposta con `Cross-Origin-Opener-Policy` diversa da `unsafe-none` va in ERRORE DI RETE: un connettore
 *   sociale il cui IdP la manda fallisce dentro la finestra. La finestra allora resta aperta sulla pagina d'errore, e l'iframe se
 *   ne accorge quando la si chiude (o allo scadere): il messaggio, mai un ciclo.
 * - `web.telegram.org/a/` e `/k/` NON mandano COOP (tma-orcr, 11:06:43Z): `window.open` dall'iframe non e' forzato a `noopener`.
 * - Le piattaforme: Web A chiede la webview con `platform: 'weba'`; Web K con `'web'` (`appAttachMenuBotsManager.ts:176`), non
 *   `'webk'`. `'webk'` resta in lista per prudenza (non costa).
 *
 * ⛔ Questo modulo NON importa `lib/logto.ts` (che lo importa): le operazioni sul PKCE gli arrivano come dipendenze. E' cosi' che
 * i test lo esercitano con una finestra finta.
 */

/*
 * 🔴 IL FLAG (`VITE_FEATURE_LOGIN_FINESTRA_ENABLED`) NON SI LEGGE QUI: e' scritto LETTERALE (`=== 'true'`) in ogni punto che decide
 * (`lib/logto.ts`, `routes/AuthCallback.tsx`, i tre «Accedi»). Misurato il 31/10 su una build di produzione: con una costante
 * esportata da qui, il bundler teneva `eStatoFinestra`, `consegnaAllOpener` e lo stato dell'esito nel chunk comune (gli export fra
 * chunk si decidono prima che la costante sia piegata nei moduli che la importano). Col confronto letterale nel modulo che decide,
 * il ramo e i suoi import spariscono.
 */

export type StradaLogin = 'finestra' | 'ponte' | 'webview';

/** Le piattaforme del PONTE (browser di sistema → deep link `t.me`): le stesse di `isTelegramMobileClient`. */
export const PIATTAFORME_PONTE: readonly string[] = ['android', 'android_x', 'ios'];
/** Telegram Web: `weba` e' Web A; Web K si presenta come `web` (e `webk` per prudenza). */
export const PIATTAFORME_FINESTRA: readonly string[] = ['weba', 'web', 'webk'];

/**
 * La scelta della strada: funzione PURA della piattaforma e del flag.
 *  - android/android_x/ios → il ponte, invariato;
 *  - weba/web/webk col flag acceso → la finestra;
 *  - tutto il resto (tdesktop, macos, unigram, piattaforma ignota, e Telegram Web col flag spento) → la webview che naviga se'.
 */
export function stradaLogin(piattaforma: string | null | undefined, flagAcceso: boolean): StradaLogin {
  if (piattaforma && PIATTAFORME_PONTE.includes(piattaforma)) return 'ponte';
  if (flagAcceso && piattaforma && PIATTAFORME_FINESTRA.includes(piattaforma)) return 'finestra';
  return 'webview';
}

/**
 * Il SEGNO del flusso, in testa allo `state`. Il punto non sta nell'alfabeto base64url di `randomString`, quindi uno state delle
 * altre due strade non comincia MAI cosi'.
 */
export const PREFISSO_STATO_FINESTRA = 'qfin.';

export function eStatoFinestra(state: string | null | undefined): boolean {
  return typeof state === 'string' && state.length > PREFISSO_STATO_FINESTRA.length && state.startsWith(PREFISSO_STATO_FINESTRA);
}

/** La forma del messaggio fra la finestra e l'iframe. Una versione nel tipo: una forma diversa non e' questa. */
export const TIPO_MESSAGGIO_FINESTRA = 'qlash.login-finestra/1';

export type MessaggioFinestra = {
  tipo: typeof TIPO_MESSAGGIO_FINESTRA;
  code: string | null;
  state: string | null;
  error: string | null;
};

const stringaONull = (v: unknown): v is string | null => v === null || typeof v === 'string';

/** Legge un messaggio SOLO se ha esattamente la forma attesa; tutto il resto e' `null`. */
export function leggiMessaggioFinestra(data: unknown): MessaggioFinestra | null {
  if (typeof data !== 'object' || data === null || Array.isArray(data)) return null;
  const d = data as Record<string, unknown>;
  const chiavi = Object.keys(d).sort().join(',');
  if (chiavi !== 'code,error,state,tipo') return null;
  if (d.tipo !== TIPO_MESSAGGIO_FINESTRA) return null;
  if (!stringaONull(d.code) || !stringaONull(d.state) || !stringaONull(d.error)) return null;
  return { tipo: TIPO_MESSAGGIO_FINESTRA, code: d.code, state: d.state, error: d.error };
}

export type EsitoFinestra = 'ok' | 'bloccata' | 'chiusa' | 'scaduta' | 'rifiutata';

/** Le feature della finestra. ⛔ MAI `noopener` ne' `noreferrer`: senza opener il codice non torna. */
export const FEATURE_FINESTRA = 'popup=yes,width=480,height=720';
/** Il tempo massimo di un tentativo: poi la finestra si chiude, lo state si butta, e si riprova con un gesto nuovo. */
export const TEMPO_MASSIMO_FINESTRA_MS = 5 * 60 * 1000;
/** Ogni quanto si guarda se la finestra e' stata chiusa. Una chiusura conta al SECONDO sguardo: il messaggio puo' essere in coda. */
export const SGUARDO_FINESTRA_MS = 500;

/** Una finestra, per quel che ne serve qui (la finestra vera, o una finta nei test). */
export type FinestraAperta = {
  readonly closed: boolean;
  close(): void;
  location: { replace(url: string): void };
};

export type DipendenzeFinestra = {
  /** `window.open`. */
  apri: (url: string, nome: string, feature: string) => FinestraAperta | null;
  /** La finestra di chi apre (l'iframe): ci si mette in ascolto, e ne si legge l'origine. */
  qui: Pick<Window, 'addEventListener' | 'removeEventListener'> & { location: { origin: string } };
  /** `buildAuthUrl` col prefisso: scrive verifier e state nello storage dell'iframe. */
  costruisciUrl: () => Promise<string>;
  /** `exchangeInAppAuthCode`: confronta lo state PRIMA dello scambio. */
  scambia: (code: string, state: string | null) => Promise<boolean>;
  /** `discardPkceState`: un tentativo finito senza scambio non lascia verifier ne' state. */
  scarta: () => void;
  log?: (riga: string) => void;
  tempoMassimoMs?: number;
};

/**
 * Apre la finestra e aspetta UN messaggio. ⚠️ `apri` si chiama in modo SINCRONO, prima di ogni `await`: i blocchi dei popup
 * vogliono la finestra aperta dentro il gestore del tocco, e `buildAuthUrl` e' asincrona (sha256). La finestra nasce vuota e riceve
 * l'URL di Logto quando e' pronto.
 *
 * Il listener accetta un messaggio SOLO se: l'origine e' quella di questa pagina, la sorgente e' la finestra aperta QUI, la forma e'
 * quella attesa. Al primo che passa si stacca (un secondo messaggio non trova nessuno), e lo scambio passa da `scambia`, che confronta
 * lo state. Ritorna anche `annulla`, per chi apre un tentativo nuovo sopra uno vecchio.
 */
export function apriFinestraLogin(dip: DipendenzeFinestra): { esito: Promise<EsitoFinestra>; annulla: () => void } {
  const log = dip.log ?? (() => {});
  const finestra = dip.apri('', '_blank', FEATURE_FINESTRA);
  if (!finestra) {
    log('finestra: window.open ha dato null (popup bloccato)');
    return { esito: Promise.resolve('bloccata'), annulla: () => {} };
  }

  let finito = false;
  let chiusaVista = false;
  let risolvi: (e: EsitoFinestra) => void = () => {};
  const esito = new Promise<EsitoFinestra>((ok) => {
    risolvi = ok;
  });

  const smonta = (): void => {
    finito = true;
    dip.qui.removeEventListener('message', ascolta);
    clearInterval(sguardo);
    clearTimeout(scadenza);
  };
  const chiudiCon = (e: EsitoFinestra, { scarta }: { scarta: boolean }): void => {
    if (finito) return;
    smonta();
    if (scarta) dip.scarta();
    log(`finestra: esito ${e}`);
    risolvi(e);
  };

  function ascolta(ev: MessageEvent): void {
    if (finito) return;
    if (ev.origin !== dip.qui.location.origin) return;
    if (ev.source !== (finestra as unknown)) return;
    const m = leggiMessaggioFinestra(ev.data);
    if (!m) return;
    // UN messaggio: ci si stacca prima di ogni altra cosa.
    smonta();
    if (m.error || !m.code) {
      dip.scarta();
      log(`finestra: ritorno senza codice (${m.error ? 'errore di Logto' : 'code assente'})`);
      risolvi('rifiutata');
      return;
    }
    dip.scambia(m.code, m.state).then(
      (ok) => risolvi(ok ? 'ok' : 'rifiutata'),
      () => risolvi('rifiutata'),
    );
  }

  dip.qui.addEventListener('message', ascolta);
  const sguardo = setInterval(() => {
    if (!finestra.closed) return;
    if (!chiusaVista) {
      chiusaVista = true;
      return;
    }
    chiudiCon('chiusa', { scarta: true });
  }, SGUARDO_FINESTRA_MS);
  const scadenza = setTimeout(() => {
    try {
      finestra.close();
    } catch {
      /* gia' chiusa */
    }
    chiudiCon('scaduta', { scarta: true });
  }, dip.tempoMassimoMs ?? TEMPO_MASSIMO_FINESTRA_MS);

  dip.costruisciUrl().then(
    (url) => {
      if (finito || finestra.closed) return;
      log('finestra: aperta, le do l’URL di Logto');
      finestra.location.replace(url);
    },
    () => {
      try {
        finestra.close();
      } catch {
        /* gia' chiusa */
      }
      chiudiCon('rifiutata', { scarta: true });
    },
  );

  return {
    esito,
    annulla: () => {
      if (finito) return;
      try {
        finestra.close();
      } catch {
        /* gia' chiusa */
      }
      // Senza `scarta`: il tentativo nuovo ha gia' scritto il SUO verifier e il suo state.
      chiudiCon('chiusa', { scarta: false });
    },
  };
}

/* ── l'esito per lo schermo: chi mostra «Accedi» mostra anche il messaggio ── */

let esitoCorrente: Exclude<EsitoFinestra, 'ok'> | null = null;
const ascoltatori = new Set<() => void>();
const avvisa = (): void => ascoltatori.forEach((f) => f());

export function esitoLoginFinestra(): Exclude<EsitoFinestra, 'ok'> | null {
  return esitoCorrente;
}
export function iscrivitiEsitoLoginFinestra(f: () => void): () => void {
  ascoltatori.add(f);
  return () => ascoltatori.delete(f);
}
function impostaEsito(e: Exclude<EsitoFinestra, 'ok'> | null): void {
  if (esitoCorrente === e) return;
  esitoCorrente = e;
  avvisa();
}

let tentativoInCorso: (() => void) | null = null;

/**
 * Il tentativo vero, con la finestra vera. Toglie il messaggio di prima, chiude un tentativo ancora aperto, apre (sincrono) e
 * aspetta. Il messaggio resta finche' non parte un tentativo nuovo; su `ok` non c'e' messaggio (chi chiama ricarica).
 */
export function accediConFinestra(
  dip: Omit<DipendenzeFinestra, 'apri' | 'qui'> & Partial<Pick<DipendenzeFinestra, 'apri' | 'qui'>>,
): Promise<EsitoFinestra> {
  impostaEsito(null);
  tentativoInCorso?.();
  tentativoInCorso = null;
  const { esito, annulla } = apriFinestraLogin({
    apri: (u, n, f) => window.open(u, n, f),
    qui: window,
    ...dip,
  });
  tentativoInCorso = annulla;
  return esito.then((e) => {
    if (tentativoInCorso === annulla) tentativoInCorso = null;
    if (e !== 'ok') impostaEsito(e);
    return e;
  });
}

/* ── la meta' nella FINESTRA: il callback ── */

export type Consegna = 'consegnato' | 'senza-opener';

/**
 * Nella finestra, su `/auth/callback` con lo state del flusso: manda `{code, state, error}` all'opener, SOLO all'origine di questa
 * pagina (mai `'*'`), e chiude. Senza opener (finestra aperta a mano, opener perso) NON fa niente: nessuno scambio qui, nessuno
 * storage, nessun ciclo — chi chiama mostra «torna alla TMA e riprova».
 */
export function consegnaAllOpener(w: Pick<Window, 'opener' | 'location' | 'close'>): Consegna {
  const opener = w.opener as Window | null;
  if (!opener || opener.closed) return 'senza-opener';
  const q = new URLSearchParams(w.location.search);
  const msg: MessaggioFinestra = {
    tipo: TIPO_MESSAGGIO_FINESTRA,
    code: q.get('code'),
    state: q.get('state'),
    error: q.get('error'),
  };
  opener.postMessage(msg, w.location.origin);
  w.close();
  return 'consegnato';
}
